import type { ChangeEvent } from 'react';
import React, { useEffect, useReducer } from 'react';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { type QuestionType, validationTypes, questionTypes, NumberSubType, InputSubType } from '../../../reducers';
import { LabelSmall } from 'baseui/typography';
import { Checkbox } from 'baseui/checkbox';
import { Input } from 'baseui/input';
import { validationReducer } from './reducers';
import { ValidationRow } from './styled-components';
import { getValidationByType } from './utils';
import { AcceptedFileTypesValidation } from './AcceptedFileTypesValidation';
import { MaxFileSizeValidation } from './MaxFileSizeValidation';
import { createValidationTag, getValidationFromTags, getValidationTypeAndValue } from '../../../reducers/selectors';
type QuestionValidationProps = {
  question: QuestionType;
  editMode: boolean;
  updateQuestion: Function;
};
export function QuestionValidation({
  question,
  updateQuestion,
  editMode
}: QuestionValidationProps) {
  const [validationState, dispatch] = useReducer(validationReducer, question.validations || []);
  const removeTag = (tag: any) => {
    const tags = get(question, 'tags') || [];
    updateQuestion({
      ...question,
      tags: tags.filter(t => t !== tag)
    });
  };
  const tags = get(question, 'tags') || [];
  const subType = (tags.find(tag => tag === NumberSubType.rating_slider) as NumberSubType | undefined);
  const disablePaste = (tags.find(tag => tag === InputSubType.shouldDisablePaste) as InputSubType | undefined);
  const dynamicSizeInput = (tags.find(tag => tag === InputSubType.hasDynamicSizing) as InputSubType | undefined);
  useEffect(() => {
    const containsRatingSliderValidation = validationState.some(validation => validation?.type === validationTypes.MINVAL) && validationState.some(validation => validation?.type === validationTypes.MAXVAL);
    if (subType === NumberSubType.rating_slider && !containsRatingSliderValidation) {
      dispatch({
        type: 'ADD_VALIDATION',
        value: validationTypes.MINVAL
      });
      dispatch({
        type: 'ADD_VALUE',
        kind: validationTypes.MINVAL,
        value: '1'
      });
      dispatch({
        type: 'ADD_VALIDATION',
        value: validationTypes.MAXVAL
      });
      dispatch({
        type: 'ADD_VALUE',
        kind: validationTypes.MAXVAL,
        value: '5'
      });
    }
  }, [subType, validationState]);
  useEffect(() => {
    updateQuestion({
      ...question,
      validations: validationState
    });
  }, [validationState]);
  if (!editMode) {
    return null;
  }
  const regexValidation = getValidationByType(validationState, validationTypes.REGEXP);
  const minvalValidation = getValidationByType(validationState, validationTypes.MINVAL);
  const maxvalValidation = getValidationByType(validationState, validationTypes.MAXVAL);
  const minWordValidation = getValidationFromTags(tags, validationTypes.MIN_WORDS);
  const maxWordValidation = getValidationFromTags(tags, validationTypes.MAX_WORDS);
  return <div>
      {[questionTypes.input, questionTypes.number, questionTypes.file, questionTypes.richText]
    /* @ts-expect-error todo(ts-migration) TS2345 Argument of type 'QuestionTypeType' is not assignable to parameter of type '"number" | "input" | "file"'. */.includes(question.type) && <LabelSmall>Validations</LabelSmall>}
      {(question.type === questionTypes.input || question.type === questionTypes.richText) && <ValidationRow>
          <Checkbox checked={validationState.some(validation => {
        return validation ? validation.type === validationTypes.REGEXP : false;
      })} onChange={e => {
        if (e.target.checked) {
          dispatch({
            type: 'ADD_VALIDATION',
            value: validationTypes.REGEXP
          });
        } else {
          dispatch({
            type: 'REMOVE_VALIDATION',
            value: validationTypes.REGEXP
          });
        }
      }}>
            Regular expression
          </Checkbox>{' '}
          {!!regexValidation && <>
              <LabelSmall>
                (Note: This is a power user feature, please verify your regex at{' '}
                <a href="https://regex101.com/" target="blank">
                  regex101.com
                </a>
                )
              </LabelSmall>

              <Input placeholder="Enter regexp here" required value={regexValidation?.value ?? ''} onChange={e => {
          dispatch({
            type: 'ADD_VALUE',
            kind: validationTypes.REGEXP,
            value: e.target.value
          });
        }} />
            </>}
          <Checkbox checked={!!dynamicSizeInput} onChange={e => {
        if (e.target.checked) {
          const nextTags = get(question, 'tags');
          nextTags.push(InputSubType.hasDynamicSizing);
          updateQuestion({
            ...question,
            tags: uniq(nextTags)
          });
        } else {
          removeTag(InputSubType.hasDynamicSizing);
        }
      }}>
            Enable Dynamic Sizing
          </Checkbox>
          <Checkbox checked={!!disablePaste} onChange={e => {
        if (e.target.checked) {
          const nextTags = get(question, 'tags');
          nextTags.push(InputSubType.shouldDisablePaste);
          updateQuestion({
            ...question,
            tags: uniq(nextTags)
          });
        } else {
          removeTag(InputSubType.shouldDisablePaste);
        }
      }}>
            Disable Text Paste
          </Checkbox>
          <Checkbox checked={!!minWordValidation} onChange={(e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          const nextTags = tags.filter(tag => tag !== minWordValidation);
          nextTags.push(createValidationTag(validationTypes.MIN_WORDS));
          updateQuestion({
            ...question,
            tags: nextTags
          });
        } else {
          removeTag(minWordValidation);
        }
      }}>
            Minimum Word Count
          </Checkbox>{' '}
          {!!minWordValidation && <Input type="number" required placeholder="Enter minimum word count" value={getValidationTypeAndValue(minWordValidation)?.value ?? ''} onChange={(e: ChangeEvent<HTMLInputElement>) => {
        // TODO: can handle case for NaN
        let value = e.target.value;
        const nextTags = tags.filter(tag => tag !== minWordValidation);
        nextTags.push(createValidationTag(validationTypes.MIN_WORDS, value));
        updateQuestion({
          ...question,
          tags: nextTags
        });
      }} />}
          <Checkbox checked={!!maxWordValidation} onChange={(e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          const nextTags = tags.filter(tag => tag !== maxWordValidation);
          nextTags.push(createValidationTag(validationTypes.MAX_WORDS));
          updateQuestion({
            ...question,
            tags: nextTags
          });
        } else {
          removeTag(maxWordValidation);
        }
      }}>
            Maximum Word Count
          </Checkbox>{' '}
          {!!maxWordValidation && <Input type="number" required placeholder="Enter maximum word count" value={getValidationTypeAndValue(maxWordValidation)?.value ?? ''} onChange={(e: ChangeEvent<HTMLInputElement>) => {
        // TODO: can handle case for NaN
        let value = e.target.value;
        const nextTags = tags.filter(tag => tag !== maxWordValidation);
        nextTags.push(createValidationTag(validationTypes.MAX_WORDS, value));
        updateQuestion({
          ...question,
          tags: nextTags
        });
      }} />}
        </ValidationRow>}
      {question.type === 'number' && <ValidationRow>
          <Checkbox checked={validationState.some(validation => {
        return validation ? validation.type === validationTypes.MINVAL : false;
      })} onChange={e => {
        if (e.target.checked) {
          dispatch({
            type: 'ADD_VALIDATION',
            value: validationTypes.MINVAL
          });
        } else {
          dispatch({
            type: 'REMOVE_VALIDATION',
            value: validationTypes.MINVAL
          });
        }
      }}>
            Minimum Value
          </Checkbox>{' '}
          {!!minvalValidation && <Input type="number" required placeholder="Enter minimum value here" value={minvalValidation?.value ?? ''} onChange={e => {
        dispatch({
          type: 'ADD_VALUE',
          kind: validationTypes.MINVAL,
          value: e.target.value
        });
      }} />}
        </ValidationRow>}
      {question.type === 'number' && <ValidationRow>
          <Checkbox checked={validationState.some(validation => {
        return validation ? validation.type === validationTypes.MAXVAL : false;
      })} onChange={e => {
        if (e.target.checked) {
          dispatch({
            type: 'ADD_VALIDATION',
            value: validationTypes.MAXVAL
          });
        } else {
          dispatch({
            type: 'REMOVE_VALIDATION',
            value: validationTypes.MAXVAL
          });
        }
      }}>
            Maximum value
          </Checkbox>{' '}
          {!!maxvalValidation && <Input type="number" required placeholder="Enter maximum value here" value={maxvalValidation?.value ?? ''} onChange={e => {
        dispatch({
          type: 'ADD_VALUE',
          kind: validationTypes.MAXVAL,
          value: e.target.value
        });
      }} />}
        </ValidationRow>}
      {question.type === questionTypes.file && <>
          <AcceptedFileTypesValidation question={question} validationState={validationState} dispatch={dispatch} />
          <MaxFileSizeValidation question={question} validationState={validationState} dispatch={dispatch} />
        </>}
    </div>;
}